import React, { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Typography,
  Box,
  Alert,
  Divider,
  List,
  ListItem,
  ListItemText,
  IconButton,
  Chip
} from '@mui/material';
import PersonAddIcon from '@mui/icons-material/PersonAdd';
import DeleteIcon from '@mui/icons-material/Delete';
import EmailIcon from '@mui/icons-material/Email';

/**
 * InviteMemberDialog Component
 *
 * Modal for inviting new team members to the organization.
 *
 * Features:
 * - Email input with validation
 * - Role selection for invitee
 * - Multiple invitations in one session
 * - Email preview
 * - Invitation tracking
 *
 * Props:
 * - open: boolean - Dialog open state
 * - onClose: function - Close handler
 * - organizationName: string - Organization name for invitation email
 * - currentUserRole: string - Current user's role for permission checks
 * - memberLimit: number - Maximum members allowed
 * - currentMemberCount: number - Current active member count
 * - onSendInvites: function - Handler to send invitations (invitations array)
 */
function InviteMemberDialog({
  open,
  onClose,
  organizationName,
  currentUserRole,
  memberLimit,
  currentMemberCount,
  onSendInvites
}) {
  const [email, setEmail] = useState('');
  const [selectedRole, setSelectedRole] = useState('viewer');
  const [invitations, setInvitations] = useState([]);
  const [emailError, setEmailError] = useState('');
  const [sending, setSending] = useState(false);
  const [error, setError] = useState('');

  // Available roles for invitation (matching backend OrganizationRole enum)
  const roles = [
    { value: 'viewer', label: 'Viewer', description: 'Read-only access to all data' },
    { value: 'pilot', label: 'Pilot', description: 'Can execute missions and view fleet' },
    { value: 'manager', label: 'Manager', description: 'Can manage missions and fleet, invite members' },
    { value: 'admin', label: 'Admin', description: 'Can manage members, settings, and all resources' },
    { value: 'owner', label: 'Owner', description: 'Full control, can delete organization (use with caution)' }
  ];

  // Filter roles based on current user permissions
  const canInviteAsOwner = currentUserRole === 'owner';
  const availableRoles = roles.filter(role => {
    if (role.value === 'owner' && !canInviteAsOwner) return false;
    return true;
  });

  // Reset state when dialog opens
  React.useEffect(() => {
    if (open) {
      setEmail('');
      setSelectedRole('viewer');
      setInvitations([]);
      setEmailError('');
      setError('');
    }
  }, [open]);

  // Email validation
  const validateEmail = (email) => {
    const emailPattern = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailPattern.test(email);
  };

  // Check if adding more members would exceed limit
  const remainingSlots = memberLimit - currentMemberCount - invitations.length;
  const isAtLimit = remainingSlots <= 0;

  const handleEmailChange = (e) => {
    setEmail(e.target.value);
    setEmailError('');
  };

  const handleAddInvitation = () => {
    // Validation
    if (!email.trim()) {
      setEmailError('Email is required');
      return;
    }

    if (!validateEmail(email.trim())) {
      setEmailError('Please enter a valid email address');
      return;
    }

    // Check for duplicates in current invitations
    if (invitations.some(inv => inv.email.toLowerCase() === email.trim().toLowerCase())) {
      setEmailError('This email has already been added to the invitation list');
      return;
    }

    // Check member limit
    if (isAtLimit) {
      setError(`Cannot add more invitations. Your plan allows ${memberLimit} members maximum.`);
      return;
    }

    // Add to invitations list
    setInvitations([
      ...invitations,
      {
        id: Date.now(),
        email: email.trim(),
        role: selectedRole
      }
    ]);

    // Reset form
    setEmail('');
    setSelectedRole('viewer');
    setEmailError('');
    setError('');
  };

  const handleRemoveInvitation = (invitationId) => {
    setInvitations(invitations.filter(inv => inv.id !== invitationId));
    setError('');
  };

  const handleSendInvites = async () => {
    if (invitations.length === 0) {
      setError('Please add at least one email to invite');
      return;
    }

    try {
      setSending(true);
      setError('');

      // Call the parent handler to send invitations
      await onSendInvites(invitations);

      // Close dialog on success
      onClose();
    } catch (err) {
      setError(err.message || 'Failed to send invitations. Please try again.');
    } finally {
      setSending(false);
    }
  };

  const handleClose = () => {
    if (!sending) {
      onClose();
    }
  };

  const getRoleColor = (role) => {
    switch (role) {
      case 'owner':
        return 'error';
      case 'admin':
        return 'warning';
      case 'manager':
        return 'info';
      case 'pilot':
        return 'success';
      case 'viewer':
        return 'default';
      default:
        return 'default';
    }
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle>
        <Box display="flex" alignItems="center" gap={1}>
          <PersonAddIcon />
          <Typography variant="h6">Invite Team Members</Typography>
        </Box>
      </DialogTitle>

      <DialogContent>
        {/* Organization Info */}
        <Box mb={3} p={2} bgcolor="#f5f5f5" borderRadius={1}>
          <Typography variant="body2" color="textSecondary" gutterBottom>
            Inviting to:
          </Typography>
          <Typography variant="h6" gutterBottom>
            {organizationName}
          </Typography>
          <Box display="flex" gap={2} mt={1}>
            <Box>
              <Typography variant="caption" color="textSecondary">
                Available Slots
              </Typography>
              <Typography variant="body1" fontWeight={600} color={isAtLimit ? 'error' : 'primary'}>
                {remainingSlots} / {memberLimit}
              </Typography>
            </Box>
            <Box>
              <Typography variant="caption" color="textSecondary">
                Pending Invites
              </Typography>
              <Typography variant="body1" fontWeight={600} color="warning.main">
                {invitations.length}
              </Typography>
            </Box>
          </Box>
        </Box>

        {/* Error Message */}
        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        {/* Member Limit Warning */}
        {isAtLimit && (
          <Alert severity="warning" sx={{ mb: 2 }}>
            You've reached your plan's member limit. Remove pending invitations or upgrade your plan to invite more members.
          </Alert>
        )}

        {/* Invitation Form */}
        <Box mb={3}>
          <Typography variant="subtitle2" gutterBottom>
            Add New Invitation
          </Typography>
          <Box display="flex" flexDirection="column" gap={2}>
            <TextField
              fullWidth
              label="Email Address"
              type="email"
              value={email}
              onChange={handleEmailChange}
              error={!!emailError}
              helperText={emailError}
              placeholder="colleague@example.com"
              disabled={sending || isAtLimit}
              InputProps={{
                startAdornment: <EmailIcon sx={{ mr: 1, color: 'action.active' }} />
              }}
              onKeyPress={(e) => {
                if (e.key === 'Enter') {
                  e.preventDefault();
                  handleAddInvitation();
                }
              }}
            />

            <FormControl fullWidth disabled={sending || isAtLimit}>
              <InputLabel>Role</InputLabel>
              <Select
                value={selectedRole}
                onChange={(e) => setSelectedRole(e.target.value)}
                label="Role"
              >
                {availableRoles.map((role) => (
                  <MenuItem key={role.value} value={role.value}>
                    <Box>
                      <Typography variant="body2">{role.label}</Typography>
                      <Typography variant="caption" color="textSecondary">
                        {role.description}
                      </Typography>
                    </Box>
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <Button
              variant="outlined"
              onClick={handleAddInvitation}
              disabled={sending || isAtLimit}
              fullWidth
            >
              Add to Invitation List
            </Button>
          </Box>
        </Box>

        <Divider sx={{ my: 2 }} />

        {/* Invitations List */}
        <Box>
          <Typography variant="subtitle2" gutterBottom>
            Pending Invitations ({invitations.length})
          </Typography>
          {invitations.length === 0 ? (
            <Box textAlign="center" p={3} bgcolor="#f9f9f9" borderRadius={1}>
              <Typography variant="body2" color="textSecondary">
                No invitations added yet. Add email addresses above to get started.
              </Typography>
            </Box>
          ) : (
            <List sx={{ bgcolor: '#f9f9f9', borderRadius: 1 }}>
              {invitations.map((invitation) => (
                <ListItem
                  key={invitation.id}
                  secondaryAction={
                    <IconButton
                      edge="end"
                      onClick={() => handleRemoveInvitation(invitation.id)}
                      disabled={sending}
                      size="small"
                    >
                      <DeleteIcon />
                    </IconButton>
                  }
                  sx={{ borderBottom: '1px solid #e0e0e0' }}
                >
                  <ListItemText
                    primary={invitation.email}
                    secondary={
                      <Chip
                        label={invitation.role.toUpperCase()}
                        color={getRoleColor(invitation.role)}
                        size="small"
                        sx={{ borderRadius: '4px', mt: 0.5 }}
                      />
                    }
                  />
                </ListItem>
              ))}
            </List>
          )}
        </Box>

        {/* Invitation Info */}
        {invitations.length > 0 && (
          <Alert severity="info" sx={{ mt: 2 }}>
            <Typography variant="body2">
              Invitation emails will be sent to {invitations.length} {invitations.length === 1 ? 'recipient' : 'recipients'}.
              They'll receive a link to accept the invitation and join {organizationName}.
            </Typography>
          </Alert>
        )}
      </DialogContent>

      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={handleClose} disabled={sending}>
          Cancel
        </Button>
        <Button
          onClick={handleSendInvites}
          variant="contained"
          color="primary"
          disabled={sending || invitations.length === 0}
          startIcon={<EmailIcon />}
        >
          {sending ? 'Sending...' : `Send ${invitations.length} ${invitations.length === 1 ? 'Invitation' : 'Invitations'}`}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

export default InviteMemberDialog;
